import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { iif, Observable, of, Subject } from 'rxjs';
import { switchMap, take, tap } from 'rxjs/operators';

import { AuthService } from '@features/auth/auth.service';
import { AssignEmployeeComponent } from '@features/project/modals/create-employee/assign-employee.component';
import { CreateTagComponent } from '@features/project/modals/create-tag/create-tag.component';
import { AddAbsenceModalComponent } from '@shared/components/add-absence-modal/add-absence-modal.component';
import { CreateChecklistModalComponent } from '@shared/components/create-checklist-modal/create-checklist-modal.component';
import { CreateDeviationModalComponent } from '@shared/components/create-deviation-modal/create-deviation-modal.component';
import { CreateTaskComponent } from '@shared/components/create-task/create-task.component';
import { ProjectsCreateComponent } from '@shared/components/projects-create/projects-create.component';
import { ImagesSliderTileComponent } from '@ui-kit/images-slider-tile/images-slider-tile.component';
import { CollectionModel, ItemModel } from 'collection';

export interface IShowImageSlider {
  imgCollection: CollectionModel;
  imageIndex: number;
  isDeleteShown?: boolean;
  item?: ItemModel;
}

@Injectable({
  providedIn: 'root'
})
export class EntityEditService {
  private _dialogRef;

  constructor(
    private _dialog: MatDialog,
    private _auth: AuthService
  ) {}

  openProjectModal({
    id,
    isSubproject,
    preSelectedData,
    estimateConvertingData,
    isWorker,
    showScheduleBtn
  }: {
    id?: number;
    isSubproject?: boolean;
    preSelectedData?: any;
    estimateConvertingData?: any;
    isWorker?: boolean;
    showScheduleBtn?: boolean;
  } = {}): Observable<any> {
    const confirmSubject = new Subject();

    this._dialogRef = this._dialog.open(ProjectsCreateComponent, {
      panelClass: 'reset-mat-modal',
      data: {
        subject: confirmSubject,
        item: {
          id,
          isSubproject: !!isSubproject
        },
        showScheduleBtn,
        isWorker,
        preSelectedData,
        estimateConvertingData
      }
    });

    const subscriber = this._dialogRef.backdropClick().subscribe(() => {
      confirmSubject.next({
        width: 'md',
        confirmed: false,
        modal: this._dialogRef
      });
      this._dialogRef.close();
      subscriber.unsubscribe();
    });

    return confirmSubject.pipe(take(1));
  }

  openChecklistModal({ id, name }: { id?: number; name?: string } = {}): Observable<any> {
    const confirmSubject = new Subject();

    this._dialogRef = this._dialog.open(CreateChecklistModalComponent, {
      width: 'md',
      panelClass: 'reset-mat-modal',
      data: {
        subject: confirmSubject,
        item: {
          id,
          name
        }
      }
    });

    const subscriber = this._dialogRef.backdropClick().subscribe(() => {
      confirmSubject.next({
        width: 'md',
        confirmed: false,
        modal: this._dialogRef
      });
      this._dialogRef.close();
      subscriber.unsubscribe();
    });

    return confirmSubject.pipe(take(1));
  }

  assignEmployee(): Subject<any> {
    const confirmSubject = new Subject();

    this._dialogRef = this._dialog.open(AssignEmployeeComponent, {
      width: '390px',
      panelClass: 'reset-mat-modal',
      data: {
        subject: confirmSubject
      }
    });

    const subscriber = this._dialogRef.backdropClick().subscribe(() => {
      confirmSubject.next({
        confirmed: false,
        modal: this._dialogRef
      });
      this._dialogRef.close();
      subscriber.unsubscribe();
    });

    return confirmSubject;
  }

  createTag(): Subject<any> {
    const confirmSubject = new Subject();

    this._dialogRef = this._dialog.open(CreateTagComponent, {
      width: 'md',
      panelClass: 'reset-mat-modal',
      data: {
        subject: confirmSubject
      }
    });

    const subscriber = this._dialogRef.backdropClick().subscribe(() => {
      confirmSubject.next({
        confirmed: false,
        modal: this._dialogRef
      });
      this._dialogRef.close();
      subscriber.unsubscribe();
    });

    return confirmSubject;
  }

  createTask(project_id: number): Subject<any> {
    const confirmSubject = new Subject();

    this._dialogRef = this._dialog.open(CreateTaskComponent, {
      width: 'md',
      panelClass: 'reset-mat-modal',
      data: {
        subject: confirmSubject,
        project_id
      }
    });

    const subscriber = this._dialogRef.backdropClick().subscribe(() => {
      confirmSubject.next({
        confirmed: false,
        modal: this._dialogRef
      });
      this._dialogRef.close();
      subscriber.unsubscribe();
    });

    return confirmSubject;
  }

  showImagesSlider(showImageSlider: IShowImageSlider): Subject<any> {
    const { imgCollection, imageIndex, item, isDeleteShown = null } = showImageSlider;
    const confirmSubject = new Subject();

    this._dialogRef = this._dialog.open(ImagesSliderTileComponent, {
      width: 'md',
      panelClass: ['reset-mat-modal', 'images-slider-modal'],
      data: {
        subject: confirmSubject,
        collection: imgCollection,
        index: imageIndex,
        item: item,
        isDeleteShown: item ? isDeleteShown : true
      }
    });

    const subscriber = this._dialogRef.backdropClick().subscribe((a) => {
      confirmSubject.next({
        confirmed: false,
        modal: this._dialogRef
      });
      this._dialogRef.close();
      subscriber.unsubscribe();
    });

    return confirmSubject;
  }

  openAbsenceModal(absence?) {
    const confirmSubject = new Subject();

    const data = {
      title: 'Edit Absence',
      buttonLabel: 'Update',
      subject: confirmSubject,
      item: absence
    };

    const authUserId = this._auth.currentUser.id;
    const initProfile$ = (res) =>
      of(res).pipe(
        tap(() => {
          this._auth.initProfile();
          return res;
        })
      );

    this._dialog.open(AddAbsenceModalComponent, {
      width: 'md',
      panelClass: 'reset-mat-modal',
      data
    });

    return confirmSubject.pipe(
      switchMap(({ action, item }) =>
        iif(() => item.user_id === authUserId, initProfile$({ action, item }), of({ action, item }))
      )
    );
  }

  openDeviationModal({ projectId }: { projectId: number }, data?): Subject<any> {
    const confirmSubject = new Subject();

    this._dialogRef = this._dialog.open(CreateDeviationModalComponent, {
      width: '900px',
      panelClass: 'reset-mat-modal',
      data: {
        item: data?.item,
        projectId: projectId,
        subject: confirmSubject
      }
    });

    const subscriber = this._dialogRef.backdropClick().subscribe(() => {
      confirmSubject.next({
        confirmed: false,
        modal: this._dialogRef
      });
      this._dialogRef.close();
      subscriber.unsubscribe();
    });

    return confirmSubject;
  }
}
